import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { FileToView } from '../components/file-processing/file-processing.component';

// Define interfaces
export interface Card {
  id: number;
  questionText: string;
  imageUrl: string;
  answerText: string;
  answerImageUrl: string;
  explanationText: string;
  explanationImageUrl: string;
  collapsed?: boolean;
  // Hindi version properties
  hindiQuestionText?: string;
  hindiImageUrl?: string;
  hindiAnswerText?: string;
  hindiAnswerImageUrl?: string;
  hindiExplanationText?: string;
  hindiExplanationImageUrl?: string;
  hindiQuestionImageUrl?: string;
  // Additional properties from API
  topicId?: string;
  yearIAS?: string;
  yearUPPSC?: string;
  yearBPSC?: string;
  yearMPPSC?: string;
  yearRPSC?: string;
  yearUKPSC?: string;
  yearCGPSC?: string;
  yearJPSC?: string;
  questionNumber?: number;
  // Custom property for file viewer
  fileData?: FileToView; // Used to store file metadata for viewing in details pane
}

// New interfaces for the topic structure API responses
export interface Subject {
  subject_id: number;
  subject_name: string;
}

export interface Level1Topic {
  topic_id: string;
  topic_name: string;
  parent_topic_id: string;
  item_level: number;
}

export interface Level2Topic {
  topic_id: string;
  topic_name: string;
  parent_topic_id: string;
  item_level: number;
}

export interface Level3Topic {
  topic_id: string;
  topic_name: string;
  parent_topic_id: string;
  item_level: number;
}

// Common topic interface for new API response
export interface TopicItem {
  topic_id: string;
  name_eng: string;
  name_hindi?: string;
  parent_topic_id: string;
  item_level: number;
}

// New unified API response interface
export interface TopicsResponse {
  parent_topic_id: string;
  current_level: number;
  next_level: number;
  children: TopicItem[];
  has_children: boolean;
  count: number;
}

export interface Level1Response {
  parent_topic_id: string;
  level1_topics: Level1Topic[];
  count: number;
}

export interface Level2Response {
  parent_topic_id: string;
  level2_topics: Level2Topic[];
  count: number;
}

export interface Level3Response {
  parent_topic_id: string;
  level3_topics: Level3Topic[];
  count: number;
}

// New interface for subjects from zero-level API
export interface SubjectZeroResponse {
  topics: TopicItem[];
  count: number;
}

// Interface for topic tree node in the new API response
export interface TopicTreeNode {
  topic_id: string;
  name_eng: string;
  name_hindi?: string;
  parent_topic_id: string;
  item_level: number;
  depth: number;
  children: TopicTreeNode[];
}

// Interface for topic tree response from new API endpoint
export interface TopicTreeResponse {
  topic_id: string;
  tree: TopicTreeNode;
}

// Interface for multiple topic trees response
export interface MultipleTopicTreesResponse {
  topic_count: number;
  trees: {
    [key: string]: TopicTreeNode | null;
  };
}

// New interface for yearwise exam data
export interface YearwiseResponse {
  exams: Exam[];
  total_exams: number;
  total_records: number;
}

// New interface for exam data
export interface Exam {
  exam_name: string;
  papers: Paper[];
}

// New interface for paper data with years
export interface Paper {
  paper_name: string;
  years: Year[];
}

// New interface for year data
export interface Year {
  id: number;
  year: number;
}

@Injectable({
  providedIn: 'root'
})
export class DataService {
  private apiUrl = `${environment.apiBaseUrl}/api/questions`; // Use API URL from environment
  private topicsApiUrl = `${environment.apiBaseUrl}/api`; // Base URL for topics API

  // Add cache property for questions
  private questionsCache: { [key: string]: Card[] } = {};

  // Add cache property for topic trees
  private topicTreeCache: { [key: string]: TopicTreeResponse } = {};

  constructor(private http: HttpClient) { }

  // Get all subjects from the new API
  getAllSubjects(): Observable<Subject[]> {
    return this.http.get<Subject[]>(`${this.topicsApiUrl}/subjects`).pipe(
      catchError(this._handleError<Subject[]>('getAllSubjects', []))
    );
  }

  // Get all subjects from the new zero-level API endpoint
  getSubjectsFromZeroLevel(): Observable<SubjectZeroResponse> {
    console.log('Fetching subjects from zero level API...');
    return this.http.get<SubjectZeroResponse>(`${this.topicsApiUrl}/topics/subjects/zero`).pipe(
      catchError(error => {
        console.error('Error fetching subjects from API:', error);
        return this._handleError<SubjectZeroResponse>('getSubjectsFromZeroLevel', { topics: [], count: 0 })(error);
      })
    );
  }

  // Get topics by parent topic ID and level using the new unified API
  getTopicsByLevel(topicId: string, level: number): Observable<TopicsResponse> {
    console.log(`Fetching topics for parent ID ${topicId} at level ${level}...`);
    return this.http.get<TopicsResponse>(`${this.topicsApiUrl}/topics/${topicId}/level/${level}`).pipe(
      catchError(error => {
        console.error(`Error fetching topics for parent ID ${topicId} at level ${level}:`, error);
        return this._handleError<TopicsResponse>('getTopicsByLevel', {
          parent_topic_id: topicId,
          current_level: level,
          next_level: level + 1,
          children: [],
          has_children: false,
          count: 0
        })(error);
      })
    );
  }

  // Get all subjects
  getSubjects(): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/subjects`).pipe(
      catchError(this._handleError<string[]>('getSubjects', []))
    );
  }

  // Get sections for a subject
  getSections(subject: string): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/subjects/${subject}/sections`).pipe(
      catchError(this._handleError<string[]>('getSections', []))
    );
  }

  // Get topics for a section
  getTopics(section: string): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/sections/${section}/topics`).pipe(
      catchError(this._handleError<string[]>('getTopics', []))
    );
  }

  // Get subtopics for a topic
  getSubtopics(topic: string): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/topics/${topic}/subtopics`).pipe(
      catchError(this._handleError<string[]>('getSubtopics', []))
    );
  }

  // Get questions for a subtopic
  getQuestions(subtopic: string): Observable<Card[]> {
    console.log(`Fetching questions from API for subtopic: ${subtopic}`);
    console.log(`API URL: ${this.apiUrl}`);

    // Check if we have this subtopic cached
    if (this.questionsCache[subtopic]) {
      console.log(`Using cached data for subtopic: ${subtopic}`);
      console.log(`Found ${this.questionsCache[subtopic].length} items in cache`);
      return of(this.questionsCache[subtopic]);
    }

    // Use the API endpoint without the subtopic parameter since it's not supported yet
    return this.http.get<any>(`${this.apiUrl}`)
      .pipe(
        map(response => {
          console.log('API Response:', response);
          console.log(`API returned ${Array.isArray(response) ? response.length : 0} items`);

          // Map the API response to our Card interface
          const mappedData = Array.isArray(response) ? response.map((item: any) => {
            // Fix Google Cloud Storage URLs if needed
            const fixUrl = (url: string) => {
              if (url && url.includes('storage.cloud.google.com')) {
                // Convert from storage.cloud.google.com to storage.googleapis.com format
                return url.replace('storage.cloud.google.com', 'storage.googleapis.com');
              }
              return url || '';
            };

            // Handle text fields that might be URLs
            const getTextContent = (textField: string) => {
              if (!textField) return 'No content available';

              // Return the actual text content
              return textField;
            };

            // Map the new API response fields to our Card interface
            return {
              id: item.ques_id || Math.random(),
              // English fields
              questionText: getTextContent(item.ques_txt_eng),
              imageUrl: fixUrl(item.ques_img_eng) || 'assets/mock-question-image.png',
              answerText: getTextContent(item.ans_txt_eng),
              answerImageUrl: fixUrl(item.ans_img_eng) || 'assets/mock-answer-image.png',
              explanationText: getTextContent(item.expl_txt_eng),
              explanationImageUrl: fixUrl(item.expl_img_eng) || 'assets/mock-expl-image.png',
              collapsed: false,
              // Hindi fields
              hindiQuestionText: getTextContent(item.ques_txt_hindi) || getTextContent(item.ques_txt_eng),
              hindiImageUrl: fixUrl(item.ques_img_hindi) || fixUrl(item.ques_img_eng) || 'assets/mock-question-image.png',
              hindiAnswerText: getTextContent(item.ans_txt_hindi) || getTextContent(item.ans_txt_eng),
              hindiAnswerImageUrl: fixUrl(item.ans_img_hindi) || fixUrl(item.ans_img_eng) || 'assets/mock-answer-image.png',
              hindiExplanationText: getTextContent(item.expl_txt_hindi) || getTextContent(item.expl_txt_eng),
              hindiExplanationImageUrl: fixUrl(item.expl_img_hindi) || fixUrl(item.expl_img_eng) || 'assets/mock-expl-image.png',
              hindiQuestionImageUrl: fixUrl(item.ques_img_hindi) || fixUrl(item.ques_img_eng) || 'assets/mock-question-image.png',
              // Additional properties from API
              topicId: item.topic_id,
              yearIAS: item.year_ias,
              yearUPPSC: item.year_uppsc,
              yearBPSC: item.year_bpsc,
              yearMPPSC: item.year_mppsc,
              yearRPSC: item.year_rpsc,
              yearUKPSC: item.year_ukpsc,
              yearCGPSC: item.year_cgpsc,
              yearJPSC: item.year_jpsc,
              questionNumber: item.question_number
            };
          }) : [];

          console.log(`Mapped ${mappedData.length} questions from API data`);

          // Save to cache
          this.questionsCache[subtopic] = mappedData;

          return mappedData;
        }),
        catchError((error) => {
          console.error('API Error:', error);
          return this._handleError<Card[]>('getQuestions', [])(error);
        })
      );
  }

  // Get questions by topic ID
  getQuestionsByTopicId(topicId: string): Observable<Card[]> {
    console.log(`Fetching questions for topic ID: ${topicId}`);
    return this.http.get<any>(`${this.topicsApiUrl}/questions/bytopic/${topicId}`)
      .pipe(
        map(response => {
          console.log('API Response for questions by topic ID:', response);

          if (!response || !response.questions || !Array.isArray(response.questions)) {
            console.warn('No questions found or invalid response format for topic ID:', topicId);
            return [];
          }

          // Map the API response to our Card interface
          const mappedData = response.questions.map((item: any) => {
            // Fix Google Cloud Storage URLs if needed
            const fixUrl = (url: string) => {
              if (url && url.includes('storage.cloud.google.com')) {
                // Convert from storage.cloud.google.com to storage.googleapis.com format
                return url.replace('storage.cloud.google.com', 'storage.googleapis.com');
              }
              return url || '';
            };

            // Handle text fields that might be URLs
            const getTextContent = (textField: string) => {
              if (!textField) return 'No content available';
              // Return the actual text content
              return textField;
            };

            // Map the API response fields to our Card interface
            return {
              id: item.ques_id || Math.random(),
              // English fields
              questionText: getTextContent(item.ques_txt_eng),
              imageUrl: fixUrl(item.ques_img_eng) || 'assets/mock-question-image.png',
              answerText: getTextContent(item.ans_txt_eng),
              answerImageUrl: fixUrl(item.ans_img_eng) || 'assets/mock-answer-image.png',
              explanationText: getTextContent(item.expl_txt_eng),
              explanationImageUrl: fixUrl(item.expl_img_eng) || 'assets/mock-expl-image.png',
              collapsed: false,
              // Hindi fields
              hindiQuestionText: getTextContent(item.ques_txt_hindi) || getTextContent(item.ques_txt_eng),
              hindiImageUrl: fixUrl(item.ques_img_hindi) || fixUrl(item.ques_img_eng) || 'assets/mock-question-image.png',
              hindiAnswerText: getTextContent(item.ans_txt_hindi) || getTextContent(item.ans_txt_eng),
              hindiAnswerImageUrl: fixUrl(item.ans_img_hindi) || fixUrl(item.ans_img_eng) || 'assets/mock-answer-image.png',
              hindiExplanationText: getTextContent(item.expl_txt_hindi) || getTextContent(item.expl_txt_eng),
              hindiExplanationImageUrl: fixUrl(item.expl_img_hindi) || fixUrl(item.expl_img_eng) || 'assets/mock-expl-image.png',
              hindiQuestionImageUrl: fixUrl(item.ques_img_hindi) || fixUrl(item.ques_img_eng) || 'assets/mock-question-image.png',
              // Additional properties from API
              topicId: item.topic_id,
              yearIAS: item.year_ias,
              yearUPPSC: item.year_uppsc,
              yearBPSC: item.year_bpsc,
              yearMPPSC: item.year_mppsc,
              yearRPSC: item.year_rpsc,
              yearUKPSC: item.year_ukpsc,
              yearCGPSC: item.year_cgpsc,
              yearJPSC: item.year_jpsc,
              questionNumber: item.question_number
            };
          });

          console.log(`Mapped ${mappedData.length} questions from topic ID: ${topicId}`);
          return mappedData;
        }),
        catchError(error => {
          console.error(`Error fetching questions for topic ID ${topicId}:`, error);
          return this._handleError<Card[]>('getQuestionsByTopicId', [])(error);
        })
      );
  }

  // Get all topic trees in a single API call
  getAllTopicTrees(topicIds: number[]): Observable<MultipleTopicTreesResponse> {
    console.log(`Fetching all topic trees for IDs: ${topicIds.join(', ')}...`);
    return this.http.post<MultipleTopicTreesResponse>(`${this.topicsApiUrl}/topics/trees`, { topicIds }).pipe(
      catchError(error => {
        console.error(`Error fetching all topic trees:`, error);
        return this._handleError<MultipleTopicTreesResponse>('getAllTopicTrees', {
          topic_count: 0,
          trees: {}
        })(error);
      })
    );
  }

  // Get complete topics hierarchy
  getCompleteTopicsHierarchy(): Observable<MultipleTopicTreesResponse> {
    console.log('Fetching complete topics hierarchy...');
    return this.http.get<MultipleTopicTreesResponse>(`${this.topicsApiUrl}/topics/trees`).pipe(
      catchError(error => {
        console.error('Error fetching complete topics hierarchy:', error);
        return this._handleError<MultipleTopicTreesResponse>('getCompleteTopicsHierarchy', {
          topic_count: 0,
          trees: {}
        })(error);
      })
    );
  }

  // Get yearwise exam data
  getYearwiseData(): Observable<YearwiseResponse> {
    return this.http.get<YearwiseResponse>(`${environment.apiBaseUrl}/api/yearwise`).pipe(
      catchError(this._handleError<YearwiseResponse>('getYearwiseData', { exams: [], total_exams: 0, total_records: 0 }))
    );
  }

  // Error handling
  private _handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      console.error(`${operation} failed: ${error.message}`);
      // Let the app keep running by returning an empty result
      return of(result as T);
    };
  }
}
